import React, {useState, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Editor} from '@tinymce/tinymce-react';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import Instructions from './Instructions';
import SnackbarSuccess from './SnackbarSuccess';
import AutoCompleteTags from './AutoCompleteTags';
import SelectMyExerciseDialog from './SelectMyExerciseDialog';
import {BluePillButton} from '../../common/components/BluePillButton';
import {APP_READING, APP_WRITING, FEATURES} from '../../constants';
import {createExercise, updateExercise} from '../../redux/slices/builder';
import {selectUserGroup} from '../../redux/slices/user';
import {faMagnifyingGlass} from '@fortawesome/pro-regular-svg-icons';

import baseStyles from '../../css/Base.module.css';

export default function CreateEditWritingExercise({exercise}) {
  const dispatch = useDispatch();
  const history = useHistory();

  const editorRef = useRef(null);

  const [activeTabIndex, setActiveTabIndex] = useState(0); // eslint-disable-line no-unused-vars
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const [isSnackbarOpen, toggleSnackbar] = useState(false);
  const [mode, setMode] = useState(null); // CREATE, EDIT
  const [isSaving, toggleIsSaving] = useState(false);
  const [name, setName] = useState(null);
  const [readingExercise, setReadingExercise] = useState(null);
  const [sentenceHTML, setSentenceHTML] = useState(null);
  const [sentencePlainText, setSentencePlainText] = useState(null);
  const [visibility, setVisibility] = useState('');
  const [tags, setTags] = useState([]);
  const [instructionsHTML, setInstructionsHTML] = useState(null);
  const [instructionsPlainText, setInstructionsPlainText] = useState(null);
  const [isSelectExerciseDialogOpen, setIsSelectExerciseDialogOpen] = useState(false);

  const userGroup = useSelector(selectUserGroup);
  const {
    tier: {features},
  } = userGroup;
  const visibilityFeatures = features.filter((feature) => feature.startsWith('VISIBILITY_'));

  useEffect(() => {
    if (!exercise) {
      setMode('CREATE');
      setVisibility(getDefaultVisibility());
    } else {
      setMode('EDIT');
      setExistingExerciseValues();
    }
  }, [exercise?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  function getDefaultVisibility() {
    // Auto set visibility when only one option is available
    if (visibilityFeatures.length === 1) {
      if (visibilityFeatures[0] === FEATURES.VISIBILITY_PRIVATE) return 'PRIVATE';
      if (visibilityFeatures[0] === FEATURES.VISIBILITY_USER_GROUP) return 'USER_GROUP';
      if (visibilityFeatures[0] === FEATURES.VISIBILITY_PUBLIC) return 'PUBLIC';
    } else if (visibilityFeatures.includes(FEATURES.VISIBILITY_PRIVATE)) return 'PRIVATE';
    return '';
  }

  function setExistingExerciseValues() {
    const {config} = exercise;
    setName(exercise.name); // Book title of config.readingExerciseID
    setVisibility(exercise.visibility);
    setTags(exercise.tags);
    setReadingExercise({_id: config.readingExerciseID});
    setInstructionsHTML(exercise?.instructions?.html);
    setInstructionsPlainText(exercise?.instructions?.plainText);
    setSentenceHTML(config.sentenceHTML);
    setSentencePlainText(config.sentencePlainText);
  }

  function handleVisibilityChanged(event) {
    setVisibility(event.target.value);
  }

  function isSaveButtonEnabled() {
    if (mode === 'EDIT') return !!name && !!visibility && !!sentenceHTML && !!sentencePlainText && !isSaving;
    return !!name && !!visibility && !!sentenceHTML && !!sentencePlainText && !isSaving;
  }

  async function handleSave(exercisePayload) {
    if (mode === 'CREATE') {
      const newExercise = await dispatch(
        createExercise(
          exercisePayload.name,
          exercisePayload.type,
          exercisePayload.description,
          exercisePayload.visibility,
          exercisePayload.config,
          exercisePayload.tags,
        ),
      );
      history.push(`/builder/exercise/${newExercise._id}`); // Go into "edit" mode
    } else if (mode === 'EDIT') {
      await dispatch(updateExercise(exercise._id, exercisePayload));
    }

    toggleSnackbar(true);
    toggleIsSaving(false);
  }

  function handleClickCancel() {
    history.push(`/builder/exercises?type=${APP_WRITING}`);
  }

  async function handleClickSave() {
    toggleIsSaving(true);

    const tagIDs = tags.map((tag) => tag._id);

    const exercisePayload = {
      type: APP_WRITING,
      name,
      description: null,
      visibility,
      tags: tagIDs,
      config: {
        readingExerciseID: readingExercise._id,
        sentenceHTML,
        sentencePlainText,
      },
    };
    handleSave(exercisePayload);
  }

  function handleBookSelected(exercise) {
    setReadingExercise(exercise);
    setName(exercise.name);
  }

  function handleBookRemoved() {
    setReadingExercise(null);
    setName(null);
  }

  function renderHelpDialog() {
    return (
      <Dialog open={isHelpDialogOpen} onClose={() => setIsHelpDialogOpen(false)}>
        <DialogTitle>Annotating a sentence</DialogTitle>
        <DialogContent>
          <Typography variant="caption" display="block" gutterBottom>
            Example:
          </Typography>
          <Typography display="inline" className="bg-info-300">
            The
          </Typography>{' '}
          <Typography display="inline" className="bg-success-300">
            dog
          </Typography>{' '}
          <Typography display="inline" className="bg-warning-300">
            ate
          </Typography>{' '}
          <Typography display="inline">my</Typography>{' '}
          <Typography display="inline" className="bg-danger-300">
            homework
          </Typography>
          {'.'}
          <Typography>-- -- -- -- --</Typography>
          <div>
            <Typography display="inline" className="clr-success-300">
              Green
            </Typography>{' '}
            <Typography display="inline">= Known Word</Typography>
          </div>
          <div>
            <Typography display="inline" className="clr-danger-300">
              Red
            </Typography>{' '}
            <Typography display="inline">= Elkonin Box</Typography>
          </div>
          <div>
            <Typography display="inline" className="clr-info-300">
              Blue
            </Typography>{' '}
            <Typography display="inline">= High-Frequency Word</Typography>
          </div>
          <div>
            <Typography display="inline" className="clr-warning-300">
              Yellow
            </Typography>{' '}
            <Typography display="inline">= Elkonin &amp; High-Frequency Word</Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsHelpDialogOpen(false)} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  function renderTabDetails() {
    return (
      <>
        <SelectMyExerciseDialog
          isOpen={isSelectExerciseDialogOpen}
          handleClose={() => setIsSelectExerciseDialogOpen(false)}
          exerciseType={APP_READING}
          onSelectExercise={handleBookSelected}
        />
        {renderHelpDialog()}
        <div className="p-15">
          <Grid container>
            <Grid item xs={12} md={6} xl={4}>
              <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
                Book
              </Typography>
              <Typography variant="body2" className="fnt-bold">
                Select the book associated with this exercise.
              </Typography>
              {!name ? (
                <Button
                  variant="outlined"
                  onClick={() => setIsSelectExerciseDialogOpen(true)}
                  startIcon={<FontAwesomeIcon icon={faMagnifyingGlass} size="sm" className="clr-primary" />}
                  sx={{borderRadius: 18}}
                >
                  SELECT BOOK
                </Button>
              ) : (
                <Chip label={name} onDelete={handleBookRemoved} />
              )}
            </Grid>
          </Grid>

          <Grid container className="m-t-30">
            <Grid item xs={12} md={6} xl={4}>
              <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
                Sentence
              </Typography>
              <Typography variant="body2" className="fnt-bold">
                Use the editor below to create the sentence for your exercise. Use the tools to{' '}
                <Link onClick={() => setIsHelpDialogOpen(true)} href="#">
                  annotate the sentence
                </Link>
                .
              </Typography>

              <div className="m-tb-10">
                <Typography variant="caption" display="block" gutterBottom>
                  Example:
                </Typography>
                <Typography variant="body2" display="inline" className="bg-info-300">
                  The
                </Typography>{' '}
                <Typography variant="body2" display="inline" className="bg-success-300">
                  dog
                </Typography>{' '}
                <Typography variant="body2" display="inline" className="bg-warning-300">
                  ate
                </Typography>{' '}
                <Typography variant="body2" display="inline">
                  my
                </Typography>{' '}
                <Typography variant="body2" display="inline" className="bg-danger-300">
                  homework
                </Typography>
                {'.'}
                <Typography variant="body2">-- -- -- -- --</Typography>
                <Typography variant="body2" display="inline" className="clr-success-300">
                  Green
                </Typography>{' '}
                <Typography variant="body2" display="inline">
                  = Known Word
                </Typography>
                <Typography variant="body2" display="inline">
                  &nbsp;|&nbsp;
                </Typography>
                <Typography variant="body2" display="inline" className="clr-danger-300">
                  Red
                </Typography>{' '}
                <Typography variant="body2" display="inline">
                  = Elkonin Box
                </Typography>
                <Typography variant="body2" display="inline">
                  &nbsp;|&nbsp;
                </Typography>
                <Typography variant="body2" display="inline" className="clr-info-300">
                  Blue
                </Typography>{' '}
                <Typography variant="body2" display="inline">
                  = High-Frequency Word
                </Typography>
                <Typography variant="body2" display="inline">
                  &nbsp;|&nbsp;
                </Typography>
                <Typography variant="body2" display="inline" className="clr-warning-300">
                  Yellow
                </Typography>{' '}
                <Typography variant="body2" display="inline">
                  = Elkonin &amp; High-Frequency Word
                </Typography>
              </div>

              <Editor
                tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
                onInit={(evt, editor) => {
                  editorRef.current = editor;
                }}
                value={sentenceHTML}
                onEditorChange={(value, editor) => {
                  setSentenceHTML(value);
                  setSentencePlainText(editor.getContent({format: 'text'}));
                }}
                init={{
                  height: 200,
                  menubar: false,
                  branding: false,
                  // https://www.tiny.cloud/docs/plugins/opensource
                  // https://www.tiny.cloud/docs/configure/content-appearance/#exampleusingcolor_cols
                  plugins: 'wordcount',
                  toolbar: 'undo redo | backcolor | removeformat',
                  color_map: ['6BDB7A', 'Green', 'DC6782', 'Red', '5BDAE1', 'Blue', 'FBC26A', 'Yellow'],
                  custom_colors: false,
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                }}
              />
            </Grid>
          </Grid>

          {visibilityFeatures.length > 1 && (
            <div className="m-t-30">
              <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
                Visibility
              </Typography>
              <Typography variant="body2" className="fnt-bold">
                You may keep this lesson for private use, or share for others to use.
              </Typography>
              <FormControl>
                <RadioGroup value={visibility} onChange={handleVisibilityChanged}>
                  {features.includes(FEATURES.VISIBILITY_PRIVATE) && (
                    <FormControlLabel value="PRIVATE" control={<Radio />} label="Private" />
                  )}
                  {features.includes(FEATURES.VISIBILITY_USER_GROUP) && (
                    <FormControlLabel value="USER_GROUP" control={<Radio />} label="Everyone" />
                  )}
                  {features.includes(FEATURES.VISIBILITY_PUBLIC) && (
                    <FormControlLabel value="PUBLIC" control={<Radio />} label="Public" />
                  )}
                </RadioGroup>
              </FormControl>
            </div>
          )}

          <Grid container className="m-t-30">
            <Grid item xs={12} md={6} xl={4}>
              <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
                Tags
              </Typography>
              <Typography variant="body2" className="fnt-bold">
                Create a tag system to organize and find files and other information faster.
              </Typography>
              <AutoCompleteTags tags={tags} setTags={setTags} />
            </Grid>
          </Grid>
        </div>
        <AppBar position="fixed" className="bg-gray-100" sx={{top: 'auto', bottom: 0}}>
          <Toolbar>
            <Grid container direction="row" justifyContent="center" alignItems="center">
              <Button className={baseStyles['btn-pill']} onClick={handleClickCancel}>
                Cancel
              </Button>
              <BluePillButton variant="outlined" disabled={!isSaveButtonEnabled()} onClick={handleClickSave}>
                {isSaving ? <CircularProgress size={25} /> : 'Save'}
              </BluePillButton>
            </Grid>
          </Toolbar>
        </AppBar>
        <Toolbar /> {/* Makes AppBar at the bottom of the page */}
      </>
    );
  }

  function handleChangeTab(event, newTabIndex) {
    setActiveTabIndex(newTabIndex);
  }

  async function handleSaveInstructions() {
    await dispatch(
      updateExercise(exercise._id, {instructions: {html: instructionsHTML, plainText: instructionsPlainText}}),
    );
    toggleSnackbar(true);
  }

  function renderTabInstructions() {
    return (
      <Instructions
        instructionsHTML={instructionsHTML}
        setInstructionsHTML={setInstructionsHTML}
        setInstructionsPlainText={setInstructionsPlainText}
        handleClickCancel={handleClickCancel}
        handleSaveInstructions={handleSaveInstructions}
      />
    );
  }

  return (
    <>
      <SnackbarSuccess text="Exercise Saved." isSnackbarOpen={isSnackbarOpen} toggleSnackbar={toggleSnackbar} />
      <Tabs
        className="m-b-30"
        value={activeTabIndex}
        onChange={handleChangeTab}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="DETAILS" />
        <Tab label="INSTRUCTIONS" disabled={mode === 'CREATE'} />
      </Tabs>

      {activeTabIndex === 0 && renderTabDetails()}
      {activeTabIndex === 1 && renderTabInstructions()}
    </>
  );
}
